import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle } from 'lucide-react';
import {
  createCustomer,
  Customer,
  CustomerAdditionalAddress,
  sanitizeCustomerAdditionalAddressesForSave,
} from '../lib/customers';
import CustomerAdditionalAddressesFields from './customer/CustomerAdditionalAddressesFields';
import AddressAutocompleteModal from './AddressAutocompleteModal';

interface FormData {
  type: 'person' | 'business';
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  lat?: number;
  lng?: number;
}

interface CustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (customer: Customer) => void;
  businessId: string;
  initialData?: Partial<FormData>;
  baseZIndex?: number; // Optional base z-index for layering above other modals
}

const isFormValid = (formData: FormData) => {
  // Check if all required fields are filled
  const hasFirstName = formData.first_name.trim() !== '';
  const hasLastName = formData.last_name.trim() !== '';
  const hasPhone = formData.phone.trim() !== '';

  return (
    hasFirstName && 
    hasLastName && 
    hasPhone
  );
};

function CustomerModalInner({ isOpen, onClose, onSuccess, businessId, initialData, baseZIndex = 100230 }: CustomerModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    type: 'person',
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    lat: undefined,
    lng: undefined
  });
  const [additionalAddresses, setAdditionalAddresses] = useState<CustomerAdditionalAddress[]>([]);


  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Set initial data if provided
      if (initialData) {
        setFormData(prev => ({
          ...prev,
          first_name: initialData.first_name || '',
          last_name: initialData.last_name || '',
          company: initialData.company || '',
          email: initialData.email || '',
          phone: initialData.phone || '',
          address: initialData.address || ''
        }));
      }
    }
  }, [isOpen, initialData]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Reset form data when modal closes
      setFormData({
        type: 'person',
        first_name: '',
        last_name: '',
        company: '',
        email: '',
        phone: '',
        address: '',
        lat: undefined,
        lng: undefined
      });
      setAdditionalAddresses([]);
      setError(null);
    }
  };


  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const additional_addresses = sanitizeCustomerAdditionalAddressesForSave(additionalAddresses);

      const newCustomer = await createCustomer({
        business_id: businessId,
        ...formData,
        company: formData.company.trim() || null,
        additional_addresses,
      } as any);

      onSuccess(newCustomer);
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
      style={{
        // Ensure this modal sits above SelectCustomerModal and other stacked panels
        zIndex: baseZIndex,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: baseZIndex + 10
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Create Customer
            </h2>
          </div>

          {/* Form Content */}
          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`} style={{
              paddingBottom: isMobile ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : '1rem'
            }}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Customer Information Section */}
              <div className="bg-white rounded-xl border border-gray-300">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 rounded-t-xl">
                  <h3 className="text-base font-semibold text-gray-900">Customer Information</h3>
                </div>
                <div className="p-4">
                
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Enter first name"
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Enter last name"
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company
                        <span className="text-xs text-gray-500 ml-1 font-normal">Shown on invoices</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Company / business name (optional)"
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="Enter phone number"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        placeholder="Enter email address (optional)"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address
                      </label>
                      <AddressAutocompleteModal
                        value={formData.address}
                        onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                        onSelect={(address) => {
                          setFormData(prev => ({
                            ...prev,
                            address: address.formatted,
                            lat: address.lat,
                            lng: address.lng
                          }));
                        }}
                        placeholder="Enter address (optional)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      />
                    </div>

                    <CustomerAdditionalAddressesFields
                      modalAutocomplete
                      entries={additionalAddresses}
                      onChange={setAdditionalAddresses}
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="bg-white p-6">
                <button
                  type="submit"
                  disabled={loading || !isFormValid(formData)}
                  className={`w-full inline-flex items-center justify-center px-6 py-3.5 text-base font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                    isFormValid(formData) 
                      ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Creating Customer...
                    </>
                  ) : (
                    'Create Customer'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}

// Main component - AddressAutocompleteModal handles Google Maps loading
export default function CustomerModal(props: CustomerModalProps) {
  return <CustomerModalInner {...props} />;
}